import React, { useState, useEffect, useCallback, useRef } from "react";
import { useApi } from "../hooks/useApi";
import Card from "./Card";
import {
  SocExternalActivityChart,
  SocIdentityExposureChart,
  SocNewFindingsChart,
  SocOpenedResolvedChart,
} from "./SocTrendCharts";

import { trendSources } from "./socTrendSources";
import { AREA_COLOR, NEUTRAL } from "./socChartTheme";

export const RANGES = [7, 30, 90];

// Trends for Security Operations (/soc): a 7 / 30 / 90-day selector over GET api/v2/soc/trends.
// The endpoint always returns every series; a chart is shown only when the summary tiles say its
// source is connected, so a source that is not connected never appears as a zero line.
// The range can be controlled (`days` + `onDaysChange`) so the page's What moved strip uses the same
// window; `onTrends` receives each loaded response (null on failure). Without them it works alone.
// `hideIdentity`: the page shows the identity exposure chart in its Overview row instead.
// A SOC area view narrows the section: `charts` (newFindings / external / controls / network /
// identity) picks the charts, `findings` the New findings series, `bandKeys` the external bands.
// Without them every applicable chart is shown, as on the Overview. `areaKey` colours the box's top
// strip like that area's tile.
// `pollKey` (the page's automatic refresh) reloads silently: the charts stay on screen, an auth error
// never redirects, a failure keeps them, and a tick is skipped while a read is still running.
const SocTrendsSection = ({
  tiles, days: controlledDays, onDaysChange, onTrends, reloadKey = 0, pollKey = 0, hideIdentity = false,
  charts, findings, bandKeys, areaKey = null,
}) => {
  // All hooks run before any early return (the rules-of-hooks lint is off in this repo).
  const apiCall = useApi();
  const [ownDays, setOwnDays] = useState(30);
  const days = controlledDays ?? ownDays;
  const setDays = (value) => (onDaysChange ? onDaysChange(value) : setOwnDays(value));
  const onTrendsRef = useRef(onTrends);
  onTrendsRef.current = onTrends;
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestSeq = useRef(0);
  const inFlight = useRef(0);

  const load = useCallback(async (silent = false) => {
    if (silent && inFlight.current > 0) return;
    // Only the latest request may update state: a slow 7-day response arriving after a 90-day one
    // is discarded.
    requestSeq.current += 1;
    const seq = requestSeq.current;
    inFlight.current += 1;
    if (!silent) {
      setLoading(true);
      setError(false);
    }
    try {
      // Same as the summary: a 403 is rethrown to us without a redirect, so suppressAuthRedirect is
      // needed only by the automatic refresh, and would swallow a genuine 401 session expiry otherwise.
      const url = `/soc/trends?days=${days}`;
      const data = await (silent ? apiCall("GET", url, null, false, null, true) : apiCall("GET", url));
      if (seq !== requestSeq.current) return;
      setError(false);
      setTrends(data || null);
      onTrendsRef.current?.(data || null);
    } catch {
      if (seq !== requestSeq.current || silent) return;
      setError(true);
      setTrends(null);
      onTrendsRef.current?.(null);
    } finally {
      inFlight.current -= 1;
      if (seq === requestSeq.current && !silent) setLoading(false);
    }
  }, [apiCall, days]);

  // reloadKey: the page's Refresh bumps it to refetch the same range.
  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(() => {
    if (pollKey > 0) load(true);
    // Only a new poll tick triggers a silent load; days and Refresh reload through the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollKey]);

  const sources = trendSources(tiles);
  const { defender, externalRisks } = sources;
  const bands = sources.bands.filter((b) => !bandKeys || bandKeys.includes(b.metricKey));
  const wants = (chart) => !charts || charts.includes(chart);
  // A narrowed New findings chart needs one of its series to have a source.
  const findingsDefender = defender && (!findings || findings.some((f) => f !== "external"));
  const findingsExternal = externalRisks && (!findings || findings.includes("external"));
  const showNewFindings = wants("newFindings") && (findingsDefender || findingsExternal);
  const showExternal = wants("external") && bands.length > 0;
  const showControls = wants("controls");
  const showIdentity = wants("identity") && !hideIdentity && sources.identity;
  const showNetwork = wants("network") && sources.network;
  const nothing = !showNewFindings && !showExternal && !showControls && !showIdentity && !showNetwork;

  let content;
  if (loading) {
    content = (
      <div data-testid="soc-trends-loading" className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-4 border-border border-t-accent-cyan" />
      </div>
    );
  } else if (error) {
    content = (
      <Card>
        <p className="text-content-secondary">Trends couldn't be loaded.</p>
        <button
          type="button"
          onClick={() => load()}
          className="mt-3 inline-flex items-center rounded-md border border-border bg-theme-secondary px-3 py-1.5 text-sm font-medium text-content-primary hover:border-border-hover"
        >
          Retry
        </button>
      </Card>
    );
  } else if (nothing) {
    content = <p className="text-sm text-content-muted">No trend data for this area in this period.</p>;
  } else {
    content = (
      // Three charts to a row on wide screens: new findings, external activity, control alerts;
      // network signals (and identity, when the page doesn't show it in Overview) wrap to the next row.
      <div className={`grid grid-cols-1 gap-4 ${!charts ? "lg:grid-cols-3" : charts.length > 1 ? "lg:grid-cols-2" : ""}`} data-testid="soc-trends-grid">
        {showNewFindings && (
          <SocNewFindingsChart
            points={trends?.newFindings || []}
            showDefender={findingsDefender}
            showExternal={findingsExternal}
            fields={findings}
            rangeDays={days}
            vulnerabilitySource={trends?.vulnerabilitySource}
          />
        )}
        {showExternal && <SocExternalActivityChart points={trends?.externalEvents || []} bands={bands} rangeDays={days} />}
        {/* Always shown on the Overview: the page already requires the GRC license. */}
        {showControls && <SocOpenedResolvedChart
          testId="soc-chart-control-alerts"
          title="Control alerts raised vs resolved"
          subtitle="GRC control regressions and their recoveries"
          points={trends?.controlAlerts || []}
          openedLabel="Raised"
          resolvedLabel="Resolved"
          emptyText="No control alerts in this period."
        />}
        {showIdentity && <SocIdentityExposureChart points={trends?.identityExposure || []} />}
        {showNetwork && (
          <SocOpenedResolvedChart
            testId="soc-chart-network"
            title="Network signals opened vs resolved"
            subtitle="Fortinet firewall signals"
            points={trends?.networkSignals || []}
            openedLabel="Opened"
            resolvedLabel="Resolved"
            emptyText="No network signals in this period."
          />
        )}
      </div>
    );
  }

  return (
    // One box around the heading, range buttons and charts, so the range reads as applying to these
    // charts only and not to the tile beside them (whose windows are fixed).
    <section aria-label="Trends" className="relative h-full overflow-hidden rounded-xl border border-border bg-theme-secondary p-4 pt-5 shadow-sm" data-testid="soc-trends-box">
      <span className="absolute inset-x-0 top-0 h-1" style={{ background: AREA_COLOR[areaKey] || NEUTRAL }} aria-hidden="true" />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold leading-tight text-content-primary">Trends</h2>
          <p className="text-xs text-content-muted">The range applies to the charts in this box only.</p>
        </div>
        <div role="group" aria-label="Trend range" className="inline-flex gap-1">
          {RANGES.map((range) => {
            const active = range === days;
            return (
              <button
                key={range}
                type="button"
                aria-pressed={active}
                onClick={() => setDays(range)}
                className={`rounded-md border px-3 py-1 text-sm font-medium ${
                  active
                    ? "bg-accent-cyan-muted text-accent-cyan border-accent-cyan"
                    : "text-content-secondary border-border hover:text-content-primary"
                }`}
              >
                {range} days
              </button>
            );
          })}
        </div>
      </div>
      {content}
    </section>
  );
};

export default SocTrendsSection;
